import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTruncatedTooltip } from '@/core/hooks/useTruncatedTooltip';
import { CARD_POST_EVENTS, CardPostEventName } from '@/features/publication-wall/constants/events';
import { HashtagsModal } from './HashtagsModal';

interface HashtagButtonProps {
  tag: string;
  index: number;
  onClick: (tag: string, index: number) => void;
}

const HashtagButton: React.FC<HashtagButtonProps> = ({ tag, index, onClick }) => {
  const { tooltip, showTooltip, hideTooltip } = useTruncatedTooltip({
    targetSelector: '.post-card__hashtag-text',
  });

  return (
    <button
      type="button"
      className="post-card__hashtag post-card__hashtag-btn"
      onClick={() => onClick(tag, index)}
      onMouseEnter={(e) => showTooltip(e, tag)}
      onMouseLeave={hideTooltip}
      onFocus={(e) => showTooltip(e, tag)}
      onBlur={hideTooltip}
    >
      <span className="post-card__hashtag-text">{tag}</span>
      {tooltip && (
        <span
          className="post-card__hashtag-tooltip"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.text}
        </span>
      )}
    </button>
  );
};

interface PostCardHashtagsProps {
  hashtags: string[];
  postId?: string;
  eventName?: CardPostEventName;
}

export const PostCardHashtags: React.FC<PostCardHashtagsProps> = ({
  hashtags,
  postId,
  eventName = CARD_POST_EVENTS.HASHTAG_CLICK,
}) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const elements = Array.from(container.children) as HTMLElement[];
      const buttonMore = elements.find((el) => el.classList.contains('post-card__hashtag-more'));

      if (!buttonMore) return;

      const containerWidth = container.offsetWidth;

      // Reset visibility for measurement (excludes the "more" button)
      elements.forEach((el) => { if (el !== buttonMore) el.style.display = 'inline-block'; });
      buttonMore.style.display = 'none';

      // No layout available (e.g. jsdom) — keep all buttons visible
      if (containerWidth === 0) return;

      const moreWidth = buttonMore.offsetWidth + 8; // width + gap
      let currentWidth = 0;
      let count = 0;

      for (let i = 0; i < hashtags.length; i++) {
        const el = elements[i];
        // Element may be a bds-tooltip wrapper or a button directly
        const elWidth = el.offsetWidth + 4; // width + gap (4px)

        if (currentWidth + elWidth + moreWidth > containerWidth) {
          buttonMore.style.display = 'inline-flex';
          break;
        }
        currentWidth += elWidth;
        count++;
      }

      // Apply final visibility (excludes the "more" button)
      elements.forEach((el, i) => {
        if (el !== buttonMore) {
          el.style.display = i < count ? 'inline-block' : 'none';
        }
      });
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [hashtags]);

  if (!hashtags || hashtags.length === 0) {
    return <div className="post-card__hashtags" />;
  }

  const handleHashtagClick = (tag: string, index: number) => {
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          postId,
          hashtag: tag,
          index,
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const handleOpenModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowModal(true);
  };

  return (
    <>
      <div className="post-card__hashtags" ref={containerRef}>
        {hashtags.map((tag, index) => (
          <HashtagButton
            key={`${tag}-${index}`}
            tag={tag}
            index={index}
            onClick={handleHashtagClick}
          />
        ))}
        <button
          type="button"
          className="post-card__hashtag post-card__hashtag-more"
          style={{ display: 'none' }}
          onClick={handleOpenModal}
          aria-label={t('cardPosts.moreHashtags')}
        >
          ...
        </button>
      </div>
      {showModal && (
        <HashtagsModal
          hashtags={hashtags}
          onClose={() => setShowModal(false)}
          onHashtagClick={handleHashtagClick}
        />
      )}
    </>
  );
};
